import React from "react";
import type { TextProps as RACTextProps } from "react-aria-components";
import { tv, type VariantProps } from "tailwind-variants";

export const textStyles = tv({
  slots: {
    base: "text-pretty",
    strong: "font-medium text-foreground",
    small: "text-sm sm:text-xs",
    link: "underline underline-offset-4",
  },
  variants: {
    size: {
      "1": "text-xs",
      "2": "text-sm",
      "3": "text-base",
      "4": "text-lg",
      xs: "text-xs",
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
    },
    mono: {
      true: "tabular-nums",
    },
    truncate: {
      true: "truncate text-nowrap",
    },
    muted: {
      true: "text-muted-foreground/80",
    },
    bold: {
      true: "font-medium",
    },
    strikethrough: {
      true: "line-through",
    },
    isDanger: {
      true: "text-destructive",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

const { base, strong, small } = textStyles();

type TextProps = Omit<RACTextProps, "color"> &
  VariantProps<typeof textStyles> & {
    ref?: React.RefObject<HTMLElement>;
  };

export function Text({
  className,
  elementType,
  children,
  mono,
  size,
  truncate,
  muted,
  bold,
  isDanger,
  strikethrough,
  ref,
  ...props
}: TextProps) {
  return React.createElement(
    elementType ?? "span",
    {
      ...props,
      ref,
      className: base({
        size,
        mono,
        truncate,
        muted,
        bold,
        strikethrough,
        isDanger,
        className,
      }),
    },
    children,
  );
}

export function Strong({
  className,
  size,
  muted,
  mono,
  truncate,
  bold,
  strikethrough,
  ref,
  ...props
}: React.ComponentProps<"strong"> &
  VariantProps<typeof textStyles> & {
    ref?: React.RefObject<HTMLElement>;
  }) {
  return (
    <Text
      {...props}
      ref={ref}
      elementType="strong"
      className={strong({
        className,
        size,
        mono,
        truncate,
        muted,
        bold,
        strikethrough,
      })}
    />
  );
}

export function Small({
  className,
  ref,
  ...props
}: React.ComponentProps<"small"> & {
  ref?: React.RefObject<HTMLElement>;
}) {
  return (
    <Text
      {...props}
      ref={ref}
      elementType="small"
      className={small({ className })}
    />
  );
}
